/**
 * Lượt chạy dungeon trên @ml/core (createRun/stepRun): giữ RunState, log hành động để replay,
 * và bot debug (`botAction`). Không phụ thuộc Pixi/DOM → test được bằng vitest.
 */
import {
  botAction,
  createRun,
  linkContextOf,
  Mulberry32,
  stepRun,
  type BattleAction,
  type LinkContext,
  type RunAction,
  type RunContent,
  type RunEvent,
  type RunState,
} from '@ml/core';
import type { Config, Content } from '@ml/config';

export type RunSetup = {
  readonly config: Config;
  readonly content: Content;
  readonly heroId: string;
  readonly dungeonId: string;
  readonly seed: number;
};

export class RunController {
  state: RunState;
  /** Log hành động từ đầu lượt chạy (đủ để replay cùng seed). */
  readonly log: RunAction[] = [];
  private botRng: Mulberry32;
  private readonly content: RunContent;

  constructor(readonly setup: RunSetup) {
    this.content = setup.content;
    this.state = this.fresh();
    this.botRng = new Mulberry32(setup.seed ^ 0x5bd1e995);
  }

  private fresh(): RunState {
    const hero = this.setup.content.heroes.find((h) => h.id === this.setup.heroId);
    if (!hero) throw new RangeError(`hero không có trong content: ${this.setup.heroId}`);
    return createRun({
      hero,
      dungeonId: this.setup.dungeonId,
      seed: this.setup.seed,
      config: this.setup.config,
      content: this.content,
    });
  }

  restart(): void {
    this.state = this.fresh();
    this.log.length = 0;
    this.botRng = new Mulberry32(this.setup.seed ^ 0x5bd1e995);
  }

  get config(): Config {
    return this.setup.config;
  }

  /** Ngữ cảnh lọc tiền tố của lượt hiện tại (skill hết hồi chiêu + ult nếu đầy). */
  linkContext(): LinkContext {
    const b = this.state.battle;
    return linkContextOf(b.hero, b.heroState, this.setup.config);
  }

  step(action: RunAction): { state: RunState; events: readonly RunEvent[]; changed: boolean } {
    const before = this.state;
    const r = stepRun(before, action, this.setup.config, this.content);
    const changed = r.state !== before;
    if (changed) this.log.push(action);
    this.state = r.state;
    return { state: r.state, events: r.events, changed };
  }

  /** Nước của bot debug cho trận hiện tại (null nếu không ở pha battle / không có nước). */
  botMove(): BattleAction | null {
    if (this.state.phase !== 'battle') return null;
    return botAction(this.state.battle, this.botRng, this.setup.config, { tries: 24 });
  }
}
